import { blobResponseHandler, downloadBlob } from '@/shared/helpers/api';
import { buildExportStockToExcelEndpoint, buildGetCrossdockLookupEndpoint, buildGetStockBalanceEndpoint, buildGetStockInfoEndpoint, buildGetStockListEndpoint, buildGetStockLocationsEndpoint, buildRelocateStockItemEndpoint, stocksSharedTagTypes, } from '@/shared/modules/stocks/business';
import { baseApi } from '@/admin/baseApi';
import { t } from '@/core/localization';
import { addTimestampToFileName } from '@/core/utils/datetime';
import { removeEmptyValues } from '@/core/utils/objects';
import { getPriceByPrimeCostAndMargin } from './helpers';
// export const stockApi = stockBaseApi.enhanceEndpoints({ addTagTypes: ['Articles'] }).injectEndpoints({
export const stockApi = baseApi.enhanceEndpoints({ addTagTypes: ['Articles', ...stocksSharedTagTypes] }).injectEndpoints({
    endpoints: (builder) => ({
        getStockList: buildGetStockListEndpoint(builder),
        getStockInfo: buildGetStockInfoEndpoint(builder),
        getStockBalance: buildGetStockBalanceEndpoint(builder),
        exportStockToExcel: buildExportStockToExcelEndpoint(builder),
        getCrossdockLookup: buildGetCrossdockLookupEndpoint(builder),
        getStockLocations: buildGetStockLocationsEndpoint(builder),
        relocateStockItem: buildRelocateStockItemEndpoint(builder),
        // TODO: may be (or not) move to settings? It is used there also. (CHECK - this was copied from
        // admin/modules/stock/business also)
        // Addition: it is also used in warehouse->receipt-> stockReceipt (but with /stockType).
        // getStockDictionary: builder.query<IStockLookupItem[], { stockType: number } | void>({
        // 	query: (params) => {
        // 		return {
        // 			method: 'GET',
        // 			// url: 'stock/stocksdictionary',
        // 			url: `stock/stocksdictionary${params ? '/' + params.stockType : ''}`,
        // 		};
        // 	},
        // 	onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        // 		try {
        // 			await queryFulfilled;
        // 		} catch (error) {
        // 			dispatch(showApiErrorSnackbar({ header: t('get.stock.dictionary.request.failed'), error }));
        // 		}
        // 	},
        // 	transformResponse: (response: KeyValuePairObject) =>
        // 		mapKeyValuePairObjectToArray({
        // 			kvpObject: response,
        // 			keyName: propertyOf<IStockLookupItem>('stockId'),
        // 			valueName: propertyOf<IStockLookupItem>('stockName'),
        // 		}) as IStockLookupItem[],
        // }),
        getStockBalanceStatistics: builder.query({
            query: (body) => {
                return {
                    method: 'POST',
                    url: 'stock/info',
                    body,
                };
            },
            providesTags: ['Stock'],
            transformResponse: (response) => ({
                lookups: {
                    brands: [...response.lookups.brands],
                    articles: [...response.lookups.articles],
                    names: [...response.lookups.names],
                },
                active: response.active.map((item) => ({
                    stockItemId: item.stockItemId,
                    stockBalanceId: item.stockBalanceId,
                    // stockId: null,
                    brand: item.brand,
                    // brandId: null,
                    article: item.article,
                    detailName: item.name,
                    count: item.quantity,
                    primeCost: item.basePrice,
                    marginPercent: item.marginPercent,
                    isActive: true,
                    price: item.customerPrice,
                    weeklyStatistics: item.weeklyStatistics ? Object.assign({}, item.weeklyStatistics) : undefined,
                    monthlyStatistics: item.monthlyStatistics ? Object.assign({}, item.monthlyStatistics) : undefined,
                })),
                inactive: response.inactive.map((item) => ({
                    stockItemId: item.stockItemId,
                    stockBalanceId: item.stockBalanceId,
                    // stockId: null,
                    brand: item.brand,
                    // brandId: null,
                    article: item.article,
                    detailName: item.name,
                    count: item.quantity,
                    primeCost: item.basePrice,
                    marginPercent: item.marginPercent,
                    isActive: true,
                    price: item.customerPrice,
                    weeklyStatistics: item.weeklyStatistics ? Object.assign({}, item.weeklyStatistics) : undefined,
                    monthlyStatistics: item.monthlyStatistics ? Object.assign({}, item.monthlyStatistics) : undefined,
                })),
            }),
        }),
        addStockItem: builder.mutation({
            query: (stockItem) => ({
                method: 'POST',
                url: 'stock/createStockItem',
                body: stockItem,
            }),
            invalidatesTags: () => ['Stock', 'StockList', 'Articles'],
        }),
        updateStockBalanceMargin: builder.mutation({
            query: ({ itemsUpdate }) => ({
                method: 'POST',
                url: 'stock/updateStockBalance',
                body: itemsUpdate.map(({ stockBalanceId, marginPercent }) => ({ stockBalanceId, marginPercent })),
            }),
            // Invalidate should not be used to avoid page rerender, update is performed manually with onQueryStarted function.
            // invalidatesTags: (result, error, { itemsUpdate: items }) => items.map((item) => ({ type: 'Stock', id: item.stockItemId })),
            onQueryStarted: async (request, { dispatch, queryFulfilled, getState }) => {
                const { stockId, grouped, itemsUpdate } = request;
                try {
                    await queryFulfilled;
                    if (grouped) {
                        dispatch(stockApi.util.updateQueryData('getStockBalance', { stockId, grouped }, (draft) => {
                            const patch = draft.map((stockCell) => ({
                                ...stockCell,
                                stockItems: stockCell.stockItems.map((stockItem) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === stockItem.stockBalanceId);
                                    if (!itemUpdate)
                                        return stockItem;
                                    return {
                                        ...stockItem,
                                        marginPercent: itemUpdate.marginPercent,
                                        price: getPriceByPrimeCostAndMargin(stockItem.primeCost, itemUpdate.marginPercent),
                                    };
                                }),
                            }));
                            Object.assign(draft, patch);
                        }));
                    }
                    else {
                        const stockState = getState().stock;
                        const stockBalanceStatisticsRequest = {
                            stockId,
                            useSalesReport: stockState.useSalesStatistics,
                            ...removeEmptyValues(stockState.stockBalanceOptions),
                        };
                        dispatch(stockApi.util.updateQueryData('getStockBalanceStatistics', stockBalanceStatisticsRequest, (draft) => {
                            const patch = {
                                lookups: {
                                    brands: [...draft.lookups.brands],
                                    articles: [...draft.lookups.articles],
                                    names: [...draft.lookups.names],
                                },
                                active: draft.active.map((item) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === item.stockBalanceId);
                                    if (!itemUpdate)
                                        return item;
                                    return {
                                        ...item,
                                        marginPercent: itemUpdate.marginPercent,
                                        price: getPriceByPrimeCostAndMargin(item.primeCost, itemUpdate.marginPercent),
                                    };
                                }),
                                inactive: draft.inactive.map((item) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === item.stockBalanceId);
                                    if (!itemUpdate)
                                        return item;
                                    return {
                                        ...item,
                                        marginPercent: itemUpdate.marginPercent,
                                        price: getPriceByPrimeCostAndMargin(item.primeCost, itemUpdate.marginPercent),
                                    };
                                }),
                            };
                            Object.assign(draft, patch);
                        }));
                    }
                }
                catch {
                    // Next line to be used with optimistic update.
                    // patchResult.undo();
                    // Alternatively, on failure we can invalidate the corresponding cache tags to trigger a re-fetch:
                    dispatch(stockApi.util.invalidateTags(['Stock']));
                }
            },
        }),
        updateStockBalanceCost: builder.mutation({
            query: ({ itemsUpdate }) => ({
                method: 'POST',
                url: 'stock/updateStockBalance',
                body: itemsUpdate.map(({ stockBalanceId, primeCost }) => ({ stockBalanceId, primeCost })),
            }),
            // Invalidate should not be used to avoid page rerender, update is performed manually with onQueryStarted function.
            // invalidatesTags: (result, error, stockItems) => [{ type: 'Stock' }],
            onQueryStarted: async (request, { dispatch, queryFulfilled, getState }) => {
                const { stockId, grouped, itemsUpdate } = request;
                try {
                    await queryFulfilled;
                    if (grouped) {
                        dispatch(stockApi.util.updateQueryData('getStockBalance', { stockId, grouped }, (draft) => {
                            const patch = draft.map((stockCell) => ({
                                ...stockCell,
                                stockItems: stockCell.stockItems.map((stockItem) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === stockItem.stockBalanceId);
                                    if (!itemUpdate)
                                        return stockItem;
                                    return { ...stockItem, primeCost: itemUpdate.primeCost };
                                }),
                            }));
                            Object.assign(draft, patch);
                        }));
                    }
                    else {
                        const stockState = getState().stock;
                        const stockBalanceStatisticsRequest = {
                            stockId,
                            useSalesReport: stockState.useSalesStatistics,
                            ...removeEmptyValues(stockState.stockBalanceOptions),
                        };
                        dispatch(stockApi.util.updateQueryData('getStockBalanceStatistics', stockBalanceStatisticsRequest, (draft) => {
                            const patch = {
                                lookups: {
                                    brands: [...draft.lookups.brands],
                                    articles: [...draft.lookups.articles],
                                    names: [...draft.lookups.names],
                                },
                                active: draft.active.map((item) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === item.stockBalanceId);
                                    if (!itemUpdate)
                                        return item;
                                    return { ...item, primeCost: itemUpdate.primeCost };
                                }),
                                inactive: draft.inactive.map((item) => {
                                    const itemUpdate = itemsUpdate.find((itemUpdate) => itemUpdate.stockBalanceId === item.stockBalanceId);
                                    if (!itemUpdate)
                                        return item;
                                    return { ...item, primeCost: itemUpdate.primeCost };
                                }),
                            };
                            Object.assign(draft, patch);
                        }));
                    }
                }
                catch {
                    // Next line to be used with optimistic update.
                    // patchResult.undo();
                    // Alternatively, on failure we can invalidate the corresponding cache tags to trigger a re-fetch:
                    dispatch(stockApi.util.invalidateTags(['Stock']));
                }
            },
        }),
        // updateStockItemQuantity: builder.mutation<void, { stockItemId: number; count: number }[]>({
        updateStockItemQuantity: builder.mutation({
            query: ({ itemsUpdate }) => ({
                method: 'POST',
                url: 'stock/updateStockItem',
                body: itemsUpdate.map(({ stockItemId, count }) => ({ stockItemId, count })),
            }),
            // Invalidate should not be used to avoid page rerender, update is performed manually with onQueryStarted function.
            // invalidatesTags: (result, error, stockItems) => stockItems.map((item) => ({ type: 'StockItem', id: item.stockItemId })),
            // invalidatesTags: (result, error, stockItems) => [{ type: 'Stock' }],
            onQueryStarted: async (request, { dispatch, queryFulfilled }) => {
                const { stockId, grouped, itemsUpdate } = request;
                try {
                    await queryFulfilled;
                    dispatch(stockApi.util.updateQueryData('getStockBalance', { stockId, grouped }, (draft) => {
                        const patch = draft.map((stockCell) => ({
                            ...stockCell,
                            stockItems: stockCell.stockItems.map((stockItem) => {
                                const itemUpdate = itemsUpdate.find(
                                // (itemUpdate) => itemUpdate.stockItemId === stockItem.stockItemId && itemUpdate.stockBalanceId === stockItem.stockBalanceId
                                (itemUpdate) => itemUpdate.stockItemId === stockItem.stockItemId);
                                if (!itemUpdate)
                                    return stockItem;
                                return { ...stockItem, count: itemUpdate.count };
                            }),
                        }));
                        Object.assign(draft, patch);
                    }));
                }
                catch {
                    // Next line to be used with optimistic update.
                    // patchResult.undo();
                    // Alternatively, on failure we can invalidate the corresponding cache tags to trigger a re-fetch:
                    dispatch(stockApi.util.invalidateTags(['Stock']));
                }
            },
        }),
        // TODO: to delete later
        // // relocateStockItem: builder.mutation<void, { stockItemId: number; quantity: number; cellName: string }[]>({
        // relocateStockItem: builder.mutation<{ error: string; success: boolean }, IStockItemRelocate>({
        // 	query: (body) => ({
        // 		method: 'POST',
        // 		url: 'stock/relocate',
        // 		body,
        // 	}),
        // 	// invalidatesTags: (result, error, stockItems) => stockItems.map((item) => ({ type: 'StockItem', id: item.stockItemId })),
        // 	invalidatesTags: (result, error, stockItems) => [{ type: 'Stock' }, { type: 'StockList' }],
        // }),
        /** Activates or deactivates stock balances */
        activateStockBalance: builder.mutation({
            query: (items) => ({
                method: 'POST',
                url: 'stock/updateStockBalance',
                body: items.map(({ stockBalanceId, isActive }) => ({ stockBalanceId, isActive })),
            }),
            // invalidatesTags: (result, error, stockItems) => stockItems.map((item) => ({ type: 'StockItem', id: item.stockItemId })),
            invalidatesTags: () => [{ type: 'Stock' }],
        }),
        // delete for LIST grouping
        deleteStockBalance: builder.mutation({
            query: (body) => ({
                method: 'POST',
                url: 'stock/deleteStockBalance',
                body,
            }),
            // If item deleted, then corresponding article is not deleted in backend. No `{ type: 'Articles' }` should be invalidated.
            invalidatesTags: () => [{ type: 'Stock' }, { type: 'StockList' }],
        }),
        // delete for BY CELL grouping.
        deleteStockItems: builder.mutation({
            query: (body) => ({
                method: 'DELETE',
                url: `stock/stockitem`,
                body,
            }),
            // If item deleted, then corresponding article is not deleted in backend. No `{ type: 'Articles' }` should be invalidated.
            invalidatesTags: () => [{ type: 'Stock' }, { type: 'StockList' }],
            // TODO:  And check if Articles needed. May be it should be left in there. Articles would work if base api is used.
        }),
        uploadPricesFile: builder.mutation({
            query: ({ file, stockId }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    method: 'POST',
                    url: `stock/info/excel/${stockId}/prices`,
                    body: formData,
                    formData: true,
                };
            },
            invalidatesTags: [{ type: 'Stock' }],
        }),
        // If we want Lazy, then we should use queryFn instead of query and stockItemId as undefined to "clear" result.
        // Workaround since useLazeQuery cant skip.
        getStockItemCostHistory: builder.query({
            query: ({ stockItemId }) => ({
                method: 'GET',
                url: `stock/stockBalanceHistory/${stockItemId}`,
            }),
            // // @ts-ignore
            // queryFn: async (arg, api, extraOptions, baseQuery) => {
            // 	if (!arg.stockItemId) return { data: undefined };
            // 	const result = await baseQuery({
            // 		method: 'GET',
            // 		url: `stock/stockBalanceHistory/${arg.stockItemId}`,
            // 	});
            // 	if (result.error) return { error: result.error, data: null };
            // 	const data = result.data as (Omit<IStockItemCostHistoryItem, 'created'> & { created: string })[];
            // 	return {
            // 		data: data.map((historyItem) => ({
            // 			...historyItem,
            // 			created: new Date(historyItem.created),
            // 		})),
            // 	};
            // },
            // transformResponse not working with queryFn.
            transformResponse: (response) => response.map((historyItem) => ({
                ...historyItem,
                created: new Date(historyItem.created),
            })),
            providesTags: ['Stock'],
        }),
        exportStockStatisticsToExcel: builder.mutation({
            queryFn: async ({ stockId }, api, extraOptions, baseQuery) => {
                const stockState = api.getState().stock;
                const body = {
                    stockId,
                    useSalesReport: stockState.useSalesStatistics,
                    ...removeEmptyValues(stockState.stockBalanceOptions),
                };
                return downloadBlob({
                    queryResult: await baseQuery({
                        method: 'POST',
                        cache: 'no-cache',
                        url: 'stock/info/excel',
                        body,
                        responseHandler: blobResponseHandler,
                    }),
                    fileName: `${t('file.name.stock.statistics')} ${addTimestampToFileName()}.xlsx`,
                });
            },
        }),
    }),
    overrideExisting: false,
});
export const { useGetStockListQuery, useGetStockInfoQuery, useGetStockBalanceQuery, useGetStockBalanceStatisticsQuery, useExportStockToExcelMutation, useAddStockItemMutation, useGetStockItemCostHistoryQuery, useUpdateStockBalanceMarginMutation, useUpdateStockBalanceCostMutation, useUpdateStockItemQuantityMutation, 
// useGetStockDictionaryQuery,
useUploadPricesFileMutation, useExportStockStatisticsToExcelMutation, } = stockApi;
